/**
 * Conditional steps: a node with `show_if` is shown only when an earlier
 * answer matches. Shared by the client's form (which skips hidden steps), the
 * builder (which describes and validates the rule) and the review (which
 * leaves hidden steps out). The backend applies the same rule on submit so a
 * hidden required question never blocks the client.
 */
import type {
  OutgoingIntakeFormNode,
  OutgoingIntakeShowIf
} from "../../api/types/outgoingIntake";
import { flattenIntakeStepNodes } from "./intakeStepFlattening";

const norm = (v: unknown): string =>
  String(v ?? "")
    .trim()
    .toLowerCase();

/** Whether `value` (the answer to `show_if.question_id`) satisfies the rule. */
export const showIfMatches = (
  showIf: OutgoingIntakeShowIf,
  value: unknown
): boolean => {
  const { equals } = showIf;
  if (value === undefined || value === null || value === "") return false;
  if (typeof equals === "boolean") {
    if (typeof value === "boolean") return value === equals;
    const s = norm(value);
    return equals ? s === "true" || s === "yes" : s === "false" || s === "no";
  }
  const wanted = (Array.isArray(equals) ? equals : [equals]).map(norm);
  const given = (Array.isArray(value) ? value : [value]).map(norm);
  return given.some((g) => wanted.includes(g));
};

/** A node is visible unless its rule refers to an answer that doesn't match. */
export const isNodeVisible = (
  node: OutgoingIntakeFormNode,
  valueByQuestionId: Record<string, unknown>
): boolean => {
  const rule = node.show_if;
  if (!rule?.question_id) return true;
  return showIfMatches(rule, valueByQuestionId[rule.question_id]);
};

/** Every question node in the schema by id. */
export const questionsById = (
  schema: OutgoingIntakeFormNode[]
): Map<string, OutgoingIntakeFormNode> => {
  const out = new Map<string, OutgoingIntakeFormNode>();
  const walk = (nodes: OutgoingIntakeFormNode[]) => {
    for (const n of nodes) {
      if (n.node_type === "question") out.set(String(n.node_id), n);
      if (n.children?.length) walk(n.children);
    }
  };
  walk(schema ?? []);
  return out;
};

/** Question types a rule can depend on: ones with a short, comparable answer. */
export const CONDITION_SOURCE_TYPES = new Set([
  "single_select",
  "multi_select",
  "boolean",
  "short_text"
]);

/** The value(s) a rule expects, as the attorney reads them ("Married", "Yes"). */
export const showIfValueLabel = (
  rule: OutgoingIntakeShowIf,
  source: OutgoingIntakeFormNode | undefined
): string => {
  if (typeof rule.equals === "boolean") return rule.equals ? "Yes" : "No";
  const labelFor = (id: string) =>
    source?.options?.find((o) => String(o.option_id) === String(id))?.label ??
    String(id);
  const list = Array.isArray(rule.equals) ? rule.equals : [rule.equals];
  return list.map(labelFor).join(" or ");
};

const sourceLabel = (source: OutgoingIntakeFormNode | undefined): string =>
  String(source?.title ?? source?.prompt ?? "an earlier answer")
    .trim()
    .replace(/\?$/, "");

/** "only if Marital status is Married" for chips and tooltips. */
export const describeShowIf = (
  node: OutgoingIntakeFormNode,
  schema: OutgoingIntakeFormNode[]
): string | null => {
  const rule = node.show_if;
  if (!rule?.question_id) return null;
  const source = questionsById(schema).get(rule.question_id);
  return `only if ${sourceLabel(source)} is ${showIfValueLabel(rule, source)}`;
};

/** The short chip text: "if Married". */
export const showIfChipLabel = (
  node: OutgoingIntakeFormNode,
  schema: OutgoingIntakeFormNode[]
): string | null => {
  const rule = node.show_if;
  if (!rule?.question_id) return null;
  const source = questionsById(schema).get(rule.question_id);
  return `if ${showIfValueLabel(rule, source)}`;
};

/**
 * Why a rule can't work, or null: it must point at a question that exists and
 * comes before the conditional step.
 */
export const showIfProblem = (
  node: OutgoingIntakeFormNode,
  schema: OutgoingIntakeFormNode[]
): string | null => {
  const rule = node.show_if;
  if (!rule?.question_id) return null;
  const steps = flattenIntakeStepNodes(schema);
  const sourceIndex = steps.findIndex(
    (s) => String(s.node_id) === rule.question_id
  );
  const ownIndex = steps.findIndex(
    (s) => String(s.node_id) === String(node.node_id)
  );
  if (sourceIndex < 0) return "depends on a question that was removed";
  if (ownIndex >= 0 && sourceIndex >= ownIndex)
    return "depends on a question that comes after it";
  return null;
};
